import React from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import type { GridChartOfAccountsAuditLogProps } from "@/ui/organisms/GridChartOfAccountsAuditLog";
import GridChartOfAccountsAuditLog from "@/ui/organisms/GridChartOfAccountsAuditLog";
import GridLoader from "@/ui/molecules/GridLoader";
import TopBarOverview from "@/ui/molecules/TopBarOverview";
import ErrorMessage from "@/ui/atoms/ErrorMessage";

export const DEFAULT_COA_ID = 1;

export type ViewChartsOfAccountsAuditLogParams = {
    useState: ({ coaId }: { coaId: number }) => {
        allCoAs:
            | {
                  coaId: number;
                  name: string;
                  description: string;
              }[]
            | null;
        allCoAsRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        auditLogs: GridChartOfAccountsAuditLogProps["rowData"] | null;
        auditLogsRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        currentUserId: GridChartOfAccountsAuditLogProps["currentUserId"] | null;
        isProdaUser: boolean;
    };
};

export type ViewChartsOfAccountsAuditLogProps = {
    sx?: BoxProps["sx"];
    coaId: number;
    onClickBackButton: (params: { coaId: number }) => void;
};

export const createViewChartsOfAccountsAuditLog = ({
    useState,
}: ViewChartsOfAccountsAuditLogParams) => {
    const ViewChartsOfAccountsAuditLog: React.FC<
        ViewChartsOfAccountsAuditLogProps
    > = props => {
        const { sx, coaId, onClickBackButton, ...otherProps } = props;

        const {
            auditLogs,
            auditLogsRequestStatus,
            currentUserId,
            allCoAs,
            allCoAsRequestStatus,
            isProdaUser,
        } = useState({ coaId });

        const coa = allCoAs?.find(coa => coa.coaId === coaId);
        const noMatch = Boolean(allCoAs && allCoAs.length > 0 && !coa);

        const isLoading =
            auditLogsRequestStatus === "idle" ||
            allCoAsRequestStatus === "idle" ||
            auditLogsRequestStatus === "pending" ||
            allCoAsRequestStatus === "pending" ||
            currentUserId === null;

        const hasError =
            auditLogsRequestStatus === "rejected" ||
            allCoAsRequestStatus === "rejected" ||
            noMatch;

        const goBackIfNotProdaUser = React.useCallback(() => {
            if (isProdaUser) return;
            onClickBackButton({ coaId });
        }, [isProdaUser, onClickBackButton, coaId]);
        React.useEffect(goBackIfNotProdaUser, [goBackIfNotProdaUser]);

        const getBackIfItsDefaultCoA = React.useCallback(() => {
            if (coaId !== DEFAULT_COA_ID) return;
            onClickBackButton({ coaId: DEFAULT_COA_ID });
        }, [coaId, onClickBackButton]);
        React.useEffect(getBackIfItsDefaultCoA, [getBackIfItsDefaultCoA]);

        return (
            <Box
                data-testid="ViewChartsOfAccountsAuditLog"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    ...(sx || {}),
                }}
                {...otherProps}
            >
                <TopBarOverview
                    title={coa?.name}
                    backButtonLabel="Back to Chart of Account view"
                    onBackButtonClick={() => onClickBackButton({ coaId })}
                />
                {isLoading && <GridLoader />}
                {hasError && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            padding: "0 24px",
                        }}
                    >
                        <ErrorMessage>
                            Failed to load chart of accounts audit log. Please
                            try again.
                        </ErrorMessage>
                    </Box>
                )}
                {!isLoading && !hasError && (
                    <GridChartOfAccountsAuditLog
                        rowData={auditLogs || []}
                        currentUserId={currentUserId}
                    />
                )}
            </Box>
        );
    };

    return ViewChartsOfAccountsAuditLog;
};
